import { useEffect, useState } from "react";
import { LogOut, Users, Heart, Trophy, ShieldX, ArrowLeft, Lock, Download } from "lucide-react";
import { useAuth } from "@/lib/auth";

const API = process.env.REACT_APP_BACKEND_URL;

const T = {
    id: {
        title: "Dashboard Admin",
        loginTitle: "Area Khusus Admin",
        loginDesc: "Halaman ini hanya untuk pemilik TechFlaze. Masuk dengan akun Google Anda untuk melihat data pengunjung.",
        loginBtn: "Masuk dengan Google",
        denied: "Akses Ditolak",
        deniedDesc: "Akun Google ini tidak terdaftar sebagai admin.",
        back: "Kembali ke Beranda",
        logout: "Keluar",
        downloadCsv: "Unduh CSV",
        totalVisitors: "Pengunjung Terdaftar",
        withInterest: "Menyatakan Minat Paket",
        topPackage: "Paket Terpopuler",
        tableVisitor: "Pengunjung",
        tablePackage: "Paket Diminati",
        tableJoined: "Bergabung",
        tableNote: "Catatan",
        notePlaceholder: "cth. Sudah dihubungi",
        save: "Simpan",
        saved: "Tersimpan",
        packageOrders: "Pesanan Paket",
        ordersEmpty: "Belum ada minat paket yang tercatat.",
        empty: "Belum ada pengunjung yang login.",
        none: "—",
    },
    en: {
        title: "Admin Dashboard",
        loginTitle: "Admin Only Area",
        loginDesc: "This page is for the TechFlaze owner only. Sign in with your Google account to view visitor data.",
        loginBtn: "Sign in with Google",
        denied: "Access Denied",
        deniedDesc: "This Google account is not registered as an admin.",
        back: "Back to Homepage",
        logout: "Sign out",
        downloadCsv: "Download CSV",
        totalVisitors: "Registered Visitors",
        withInterest: "Expressed Package Interest",
        topPackage: "Most Popular Package",
        tableVisitor: "Visitor",
        tablePackage: "Interested Package",
        tableJoined: "Joined",
        tableNote: "Notes",
        notePlaceholder: "e.g. Already contacted",
        save: "Save",
        saved: "Saved",
        packageOrders: "Package Orders",
        ordersEmpty: "No package interest recorded yet.",
        empty: "No visitors have signed in yet.",
        none: "—",
    },
};

const GoogleIcon = ({ className = "h-4 w-4" }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
        <path d="M21.35 11.1h-9.17v2.73h6.51c-.33 3.81-3.5 5.44-6.5 5.44C8.36 19.27 5 16.25 5 12c0-4.1 3.38-7.28 7.17-7.28 3.12 0 4.88 1.9 4.88 1.9l2.16-2.72S14.34 2 12.17 2C6.42 2 2.03 6.8 2.03 12c0 5.05 4.13 10 10.22 10 5.35 0 9.25-3.67 9.25-9.09 0-1.15-.15-1.81-.15-1.81z" />
    </svg>
);

const LangToggle = ({ lang, setLang }) => (
    <div className="flex rounded-full border border-white/15 bg-white/5 p-1">
        {["id", "en"].map((l) => (
            <button
                key={l}
                type="button"
                data-testid={`admin-lang-${l}`}
                onClick={() => setLang(l)}
                className={`rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors duration-200 ${
                    lang === l ? "btn-flame text-white" : "text-zinc-400 hover:text-amber-300"
                }`}
            >
                {l}
            </button>
        ))}
    </div>
);

const Card = ({ children, className = "" }) => (
    <div className={`rounded-2xl border border-white/10 bg-[#121212] p-8 text-center ${className}`}>
        {children}
    </div>
);

const NoteCell = ({ user, t }) => {
    const [note, setNote] = useState(user.admin_note || "");
    const [saved, setSaved] = useState(false);

    const save = async () => {
        try {
            await fetch(`${API}/api/admin/users/${user.user_id}/note`, {
                method: "PATCH",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ note }),
            });
            setSaved(true);
            setTimeout(() => setSaved(false), 1600);
        } catch {
            /* biarkan tombol bisa ditekan ulang */
        }
    };

    return (
        <div className="flex items-center gap-2">
            <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t.notePlaceholder}
                data-testid={`admin-note-${user.user_id}`}
                className="w-44 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs text-slate-100 placeholder:text-zinc-600 outline-none transition-colors duration-200 focus:border-amber-400/60"
            />
            <button
                type="button"
                onClick={save}
                data-testid={`admin-note-save-${user.user_id}`}
                className={`rounded-lg px-3 py-2 text-xs font-semibold transition-colors duration-200 ${
                    saved
                        ? "bg-green-500/15 text-green-400"
                        : "border border-white/15 text-zinc-300 hover:border-amber-400/60 hover:text-amber-300"
                }`}
            >
                {saved ? t.saved : t.save}
            </button>
        </div>
    );
};

export default function AdminPage() {
    const { user, loading, login, logout } = useAuth();
    const [lang, setLang] = useState("id");
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const t = T[lang];

    useEffect(() => {
        if (loading || !user) return;
        fetch(`${API}/api/admin/users`, {
            credentials: "include",
        })
            .then(async (res) => {
                if (res.status === 403) throw new Error("forbidden");
                if (!res.ok) throw new Error("failed");
                setData(await res.json());
            })
            .catch((e) => setError(e.message));
    }, [user, loading]);

    const fmtDate = (d) =>
        d
            ? new Date(d).toLocaleDateString(lang === "id" ? "id-ID" : "en-US", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
              })
            : "—";

    if (loading) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-amber-400 border-t-transparent" />
            </div>
        );
    }

    if (!user) {
        return (
            <main data-testid="admin-page" className="flex min-h-screen items-center justify-center px-5 pt-24 pb-16">
                <Card className="max-w-md">
                    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-red-600/20 to-amber-500/20 text-amber-400">
                        <Lock className="h-7 w-7" />
                    </div>
                    <h1 className="mt-6 text-2xl font-bold text-slate-50">{t.loginTitle}</h1>
                    <p className="mt-3 text-sm leading-relaxed text-zinc-400">{t.loginDesc}</p>
                    <button
                        type="button"
                        data-testid="admin-login-button"
                        onClick={() => login("/admin")}
                        className="btn-flame mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-orange-900/40 transition-transform duration-200 hover:scale-[1.03]"
                    >
                        <GoogleIcon className="h-4 w-4" />
                        {t.loginBtn}
                    </button>
                    <div className="mt-6 flex justify-center">
                        <LangToggle lang={lang} setLang={setLang} />
                    </div>
                </Card>
            </main>
        );
    }

    if (error === "forbidden") {
        return (
            <main data-testid="admin-page" className="flex min-h-screen items-center justify-center px-5 pt-24 pb-16">
                <Card className="max-w-md">
                    <div data-testid="admin-denied">
                        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-red-600/15 text-red-400">
                            <ShieldX className="h-7 w-7" />
                        </div>
                        <h1 className="mt-6 text-2xl font-bold text-slate-50">{t.denied}</h1>
                        <p className="mt-3 text-sm leading-relaxed text-zinc-400">
                            {t.deniedDesc} ({user.email})
                        </p>
                    </div>
                    <button
                        type="button"
                        data-testid="admin-logout-button"
                        onClick={logout}
                        className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full border border-white/15 px-6 py-3 text-sm font-medium text-zinc-200 transition-colors duration-200 hover:border-red-500/50 hover:text-red-400"
                    >
                        <LogOut className="h-4 w-4" />
                        {t.logout}
                    </button>
                </Card>
            </main>
        );
    }

    const users = data?.users || [];
    const withInterest = users.filter((u) => u.paket_interest).length;
    const counts = users.reduce((acc, u) => {
        if (u.paket_interest) acc[u.paket_interest] = (acc[u.paket_interest] || 0) + 1;
        return acc;
    }, {});
    const topPackage =
        Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || t.none;

    const downloadCsv = () => {
        const header = [t.tableVisitor, "Email", t.tablePackage, t.tableJoined, t.tableNote];
        const rows = users.map((u) => [
            u.name,
            u.email,
            u.paket_interest || "",
            fmtDate(u.created_at),
            u.admin_note || "",
        ]);
        const csv = [header, ...rows]
            .map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(","))
            .join("\n");
        const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "pengunjung-techflaze.csv";
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <main data-testid="admin-page" className="mx-auto min-h-screen max-w-6xl px-5 pt-28 pb-20 md:px-8">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <img src="/assets/techflaze-logo.webp" alt="TechFlaze" className="h-9 w-auto" width="160" height="44" />
                    <span className="rounded-full border border-amber-400/30 bg-amber-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-amber-400">
                        {t.title}
                    </span>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <LangToggle lang={lang} setLang={setLang} />
                    <button
                        type="button"
                        data-testid="admin-download-csv"
                        onClick={downloadCsv}
                        className="inline-flex items-center gap-2 rounded-full border border-amber-400/40 bg-amber-400/10 px-4 py-2 text-sm font-medium text-amber-300 transition-colors duration-200 hover:border-amber-400/70"
                    >
                        <Download className="h-4 w-4" />
                        {t.downloadCsv}
                    </button>
                    <button
                        type="button"
                        data-testid="admin-logout-button"
                        onClick={logout}
                        className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-medium text-zinc-300 transition-colors duration-200 hover:border-red-500/50 hover:text-red-400"
                    >
                        <LogOut className="h-4 w-4" />
                        {t.logout}
                    </button>
                </div>
            </div>

            <div data-testid="admin-dashboard" className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-3">
                {[
                    { icon: Users, label: t.totalVisitors, value: users.length },
                    { icon: Heart, label: t.withInterest, value: withInterest },
                    { icon: Trophy, label: t.topPackage, value: topPackage },
                ].map(({ icon: Icon, label, value }) => (
                    <div key={label} className="card-surface flex items-center gap-4 p-6">
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-red-600/20 to-amber-500/20 text-amber-400">
                            <Icon className="h-6 w-6" />
                        </div>
                        <div>
                            <p className="text-2xl font-extrabold text-slate-50">{value}</p>
                            <p className="text-xs text-zinc-500">{label}</p>
                        </div>
                    </div>
                ))}
            </div>

            <div data-testid="admin-package-orders" className="card-surface mt-8 p-6 md:p-8">
                <h2 className="text-base font-semibold text-slate-50">{t.packageOrders}</h2>
                {withInterest === 0 ? (
                    <p className="mt-3 text-sm text-zinc-500">{t.ordersEmpty}</p>
                ) : (
                    <div className="mt-5 space-y-4">
                        {["Basic", "Profesional", "Luxury"].map((p) => {
                            const c = counts[p] || 0;
                            const max = Math.max(1, ...["Basic", "Profesional", "Luxury"].map((x) => counts[x] || 0));
                            return (
                                <div key={p} data-testid={`admin-order-${p.toLowerCase()}`}>
                                    <div className="flex items-center justify-between text-xs">
                                        <span className="font-medium text-zinc-300">{p}</span>
                                        <span className="font-semibold text-amber-400">{c}</span>
                                    </div>
                                    <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-white/5">
                                        <div
                                            className="h-full rounded-full bg-gradient-to-r from-red-600 to-amber-400 transition-[width] duration-500"
                                            style={{ width: `${Math.round((c / max) * 100)}%` }}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <div className="card-surface mt-8 overflow-hidden p-0">
                <div className="overflow-x-auto">
                    <table data-testid="admin-table" className="w-full min-w-[820px] text-left text-sm">
                        <thead>
                            <tr className="border-b border-white/10 bg-white/5">
                                <th className="px-6 py-4 font-semibold text-slate-200">{t.tableVisitor}</th>
                                <th className="px-6 py-4 font-semibold text-slate-200">{t.tablePackage}</th>
                                <th className="px-6 py-4 font-semibold text-slate-200">{t.tableJoined}</th>
                                <th className="px-6 py-4 font-semibold text-slate-200">{t.tableNote}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.length === 0 && (
                                <tr>
                                    <td colSpan={4} className="px-6 py-12 text-center text-zinc-500">
                                        {t.empty}
                                    </td>
                                </tr>
                            )}
                            {users.map((u) => (
                                <tr key={u.user_id} className="border-b border-white/5 last:border-0 hover:bg-white/5">
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-3">
                                            {u.picture ? (
                                                <img src={u.picture} alt="" className="h-8 w-8 rounded-full" referrerPolicy="no-referrer" />
                                            ) : (
                                                <span className="btn-flame flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold text-white">
                                                    {(u.name || u.email || "?")[0].toUpperCase()}
                                                </span>
                                            )}
                                            <div>
                                                <p className="font-medium text-slate-100">{u.name}</p>
                                                <p className="text-xs text-zinc-500">{u.email}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        {u.paket_interest ? (
                                            <span className="rounded-full border border-amber-400/40 bg-amber-400/10 px-3 py-1 text-xs font-semibold text-amber-300">
                                                {u.paket_interest}
                                            </span>
                                        ) : (
                                            <span className="text-zinc-600">{t.none}</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 text-zinc-400">{fmtDate(u.created_at)}</td>
                                    <td className="px-6 py-4">
                                        <NoteCell user={u} t={t} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <a
                href="/"
                data-testid="admin-back-link"
                className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-zinc-400 transition-colors duration-200 hover:text-amber-400"
            >
                <ArrowLeft className="h-4 w-4" />
                {t.back}
            </a>
        </main>
    );
}
